import * as React from 'react'
import { ChartData, ChartNode, ItemData, RawData } from './types'
import { FixedSizeList as List } from 'react-window'
import memoize from 'memoize-one'
import ItemRenderer from './ItemRenderer'
import { rowHeight } from './constants'
import { Tooltip } from 'react-tooltip'

interface Props {
    data: ChartData
    disableDefaultTooltips?: boolean
    height: number
    onChange?: (chartNode: ChartNode, uid: any) => void
    onMouseMove?: (event: React.SyntheticEvent<any>, node: RawData) => void
    onMouseOut?: (event: React.SyntheticEvent<any>, node: RawData) => void
    onMouseOver?: (event: React.SyntheticEvent<any>, node: RawData) => void
    width: number
    startNode: string
}

interface State {
    focusedNode: ChartNode
}

export default class FlameGraph extends React.PureComponent<Props, State> {
    // Select the root node by default.
    state: State = {
        focusedNode: this.props.data.nodes[this.props.startNode],
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (prevProps.startNode !== this.props.startNode) {
            const { nodes } = this.props.data
            const chartNode = nodes[this.props.startNode]

            if (chartNode) {
                this.setState({ focusedNode: chartNode })
            }
        }
    }

    // Shared context between the App and individual List item renderers.
    // Memoize this wrapper object to avoid breaking PureComponent's sCU.
    // Attach the memoized function to the instance,
    // So that multiple instances will maintain their own memoized cache.
    getItemData = memoize(
        (
            data: ChartData,
            disableDefaultTooltips: boolean,
            focusedNode: ChartNode,
            focusNode: (uid: any) => void,
            handleMouseEnter: (event: React.MouseEvent<SVGElement>, node: RawData) => void,
            handleMouseLeave: (event: React.MouseEvent<SVGElement>, node: RawData) => void,
            handleMouseMove: (event: React.MouseEvent<SVGElement>, node: RawData) => void,
            width: number,
        ): ItemData => ({
            data,
            disableDefaultTooltips,
            focusedNode,
            focusNode,
            handleMouseEnter,
            handleMouseLeave,
            handleMouseMove,
            scale: value => (value / focusedNode.width) * width,
        }),
    )

    focusNode = (uid: any) => {
        const { nodes } = this.props.data
        const chartNode = nodes[uid]

        const { onChange } = this.props
        if (typeof onChange === 'function') {
            onChange(chartNode, uid)
        }
    }

    handleMouseEnter = (event: React.SyntheticEvent<any>, rawData: RawData) => {
        const { onMouseOver } = this.props
        if (typeof onMouseOver === 'function') {
            onMouseOver(event, rawData)
        }
    }

    handleMouseLeave = (event: React.SyntheticEvent<any>, rawData: RawData) => {
        const { onMouseOut } = this.props
        if (typeof onMouseOut === 'function') {
            onMouseOut(event, rawData)
        }
    }

    handleMouseMove = (event: React.SyntheticEvent<any>, rawData: RawData) => {
        const { onMouseMove } = this.props
        if (typeof onMouseMove === 'function') {
            onMouseMove(event, rawData)
        }
    }

    render() {
        const { data, disableDefaultTooltips, height, width } = this.props
        const { focusedNode } = this.state

        const itemData = this.getItemData(
            data,
            !!disableDefaultTooltips,
            focusedNode,
            this.focusNode,
            this.handleMouseEnter,
            this.handleMouseLeave,
            this.handleMouseMove,
            width,
        )

        return (
            <>
                <List height={height} innerTagName="svg" itemCount={data.height} itemData={itemData} itemSize={rowHeight} width={width}>
                    {ItemRenderer}
                </List>
                <Tooltip id="flamegraphTooltip" place="bottom-start" className="tw-react-tooltip" />
            </>
        )
    }
}
